import React, { FC, memo, useEffect, useState } from 'react';
import Title from '@components/custom-flows/load-profile/Title';
import PercentLabelsY from '@components/custom-flows/load-profile/PercentLabelsY';
import { Controller, useFormContext } from 'react-hook-form';
import LoadProfileWidget from '@components/custom-flows/load-profile/LoadProfileWidget';
import { Field } from '@/types';
import Select from '@/components/form/Select';
import LineChart from '../charts/LineChart';
import SpinnerThree from '@/components/form/SpinnerThree';
import ApiService from '@/api';

const predefinedProfiles = [
  'G0: Gewerbe allgemein',
  'G1: Gewerbe werktags 8–18 Uhr',
  'G2: Gewerbe mit starkem bis überwiegendem Verbrauch in den Abendstunden',
  'G3: Gewerbe durchlaufend',
  'G4: Laden/Friseur',
  'G5: Bäckerei mit Backstube',
  'G6: Wochenendbetrieb',
  'G25: Gewerbe allgemein (aktualisiert 2025)',
  'H25: Haushalt (aktualisiert 2025)',
  'L25: Landwirtschaft (aktualisiert 2025)',
  'P25: Kombinationsprofil PV (aktualisiert 2025)',
  'S25: Kombinationsprofil PV-Speicher (aktualisiert 2025)',
];

const LoadProfileDailyField: FC<Field> = ({ description, ...props }) => {
  const form = useFormContext();
  const [loadProfileChartData, setLoadProfileChartData] = useState<any>(null);
  const [isLoadingStandardProfile, setIsLoadingStandardProfile] = useState(false);

  const fetchProfileData = async (profileType: string) => {
    try {
      setIsLoadingStandardProfile(true);
      const response = await ApiService.request({
        method: 'GET',
        endpoint: `/dashboard/get_loadprofile_data_for_plt/?profile_type=${encodeURIComponent(profileType)}`,
      });

      const data: { x: number[]; y: number[] } = response as any;

      const totalConsumptionEl = Number(form.getValues('electricity_information.total_consumption_el')) || 1;

      if (!data?.x || !data?.y || !Array.isArray(data.x) || !Array.isArray(data.y)) {
        setLoadProfileChartData(null);
        return;
      }

      const scaledDataY = data.y.map((value: number) => value * totalConsumptionEl);

      setLoadProfileChartData({
        labels: data.x,
        datasets: [
          {
            label: `Profil: ${profileType}`,
            data: scaledDataY,
          },
        ],
      });
    } catch (error) {
      setLoadProfileChartData(null);
    } finally {
      setIsLoadingStandardProfile(false);
    }
  };

  useEffect(() => {
    const selectedProfile = form.getValues()?.load_profile?.type_of_loadprofile;

    if (predefinedProfiles.includes(selectedProfile)) {
      fetchProfileData(selectedProfile);
    } else {
      setLoadProfileChartData(null);
    }
  }, [form.watch('load_profile.type_of_loadprofile')]);

  return (
    <div style={{ color: 'var(--input-text-color)' }}>
      {description}
      <Select
        is_required
        model="load_profile"
        category="text"
        name={'type_of_loadprofile'}
        label={'Standardlastprofile:'}
        page={'page'}
        type={'select'}
        options={['Benutzerdefiniert: Lastgang abschätzen', ...predefinedProfiles]}
        className=""
        description={
          'Wählen Sie eines der Standardlastprofile des Bundesverband der Energie- und Wasserwirtschaft (BDEW) aus, oder erzeugen Sie Ihren eigenen Lastgang über unsere Schieberegler'
        }
        accordion
      />
      {form.getValues('load_profile.type_of_loadprofile') === 'Benutzerdefiniert: Lastgang abschätzen' ? (
        <>
          <Title
            title={'Lastgangprofil Tag Tag'}
            tooltip={
              'Wie produzieren Sie an einem durchschnittlichen Produktionstag? Bei Ihrem Profil kann es sinnhaft sein, Teile der Stromerzegung einzuspeisen. Daher benötigen wir Informationen zu ihrem Energieverbauch über den Tag bzw. das Jahr hinweg.'
            }
          />
          <div className="flex flex-row max-md:overflow-x-scroll">
            <PercentLabelsY />
            <Controller
              control={form.control}
              render={({ field, fieldState, formState }) => {
                const daily_profile = field.value.split(',');

                return (
                  <LoadProfileWidget
                    onChange={field.onChange}
                    profile_name={field.name}
                    profile={daily_profile}
                    text_labels={daily_profile.map((_: number, i: number) => i)}
                  />
                );
              }}
              name={`${props.model}.${props.name}`}
            />
          </div>
        </>
      ) : isLoadingStandardProfile ? (
        <div className="w-full h-[100px] flex justify-center items-center">
          <SpinnerThree />
        </div>
      ) : (
        loadProfileChartData &&
        !isLoadingStandardProfile && (
          <div className="px-4 w-full">
            <LineChart data={loadProfileChartData} xtitle="Tag" ytitle="Verbrauch (kWh)" title="Lastprofil" />
          </div>
        )
      )}
    </div>
  );
};

export default memo(LoadProfileDailyField);
